// Taşeron portalı — aksayan (Failed / sıfır-kalemli Succeeded) bir analizin ZATEN yüklenmiş
// kaynak dosyalarını elle bağlar (plan S19-D7, tasarım review §4/§8, AC6). Üç yol buraya
// çıkar (hepsi `items: []`): doc-check erişilemedi, analiz `Failed` bitti, ya da `Succeeded`
// sıfır kalemle bitti (RC-1 V-12 — okunamayan bir tarama `UNCLASSIFIED_PAGES` üretir, tespit
// edilen SIFIR belge). Dosyalar §8'de zaten `Confirmed` — bu ekran YENİDEN YÜKLEMEZ, yalnız
// §13.6/§15.2 link ile bağlar.
//
// `UploadModal`'ın kendi elle giriş dalı BU senaryoyu KAPSAYAMAZ: o dalın "zaten onaylı"
// kalemleri (`kind: 'confirmed'`) yalnız BU OTURUMUN `DocumentAnalysisProvider` kuyruğundan
// gelir (bkz. o dosyanın SAHİPLİK SINIRI yorumu) — bir sayfa yenilemesinden SONRA kuyruk
// boştur, tek kaynak §20.1 `GET .../{uuid}`'in döndürdüğü `sources[]`'tir. Bu yüzden ayrı,
// küçük bir bağlama akışı: `DocumentUploadModal`/`PersonnelDocumentUploadModal`'ın SAF
// alan-grubu bileşenlerini ödünç alır (dosya DEĞİŞTİRİLMEZ), kendi çoklu-kaynak
// orkestrasyonunu (N grup → link, upload YOK) taşır — `UploadModal`'ın manuel dalıyla AYNI
// desen (bkz. o dosyanın D6 yorumu).
//
// **KAPSAM = HEDEF (D9 canlı — `PersonnelDocumentsPanel.tsx:286` `subject` ile `UploadModal`
// açıyor, dolayısıyla kişi-pinlenmiş aksayan analizler de vardır).** Belgeler'in TEK bileşeni
// (bu dosya) ile hem taşeron-kapsamlı hem kişi-kapsamlı bağlama yapılır — `target` prop'u HANGİ
// alan grubunun/ucun kullanılacağını seçer. Çağıran (Belgeler ya da personel paneli) HANGİ
// `linkedDocumentUuids` kümesini karşılaştıracağını zaten kendi listesinden bilir — bu dosya
// yalnız BAĞLAR, "hâlâ aksıyor mu" türetimi `StrandedAnalysesStrip`'in işidir.
//
// ⚠️ **ZATEN bağlanmış kaynak GÖSTERİLMEZ** (S19 istemci güvenlik incelemesi F3). `sources[]`
// analizin DEĞİŞMEZ kaydıdır — bir kaynak bağlandığında oradan düşmez. Çağıranın
// `linkedDocumentUuids`'i çıkarılmazsa 4 kaynağın 3'ünü bağlayıp 4'te ağ hatası alan kullanıcı,
// modalı yeniden açtığında o 3'ü TEKRAR sunulmuş görür ve kaydettiğinde AYNI `documentUuid` için
// İKİNCİ bir §13.6/§15.2 link satırı doğar — uygunluk listesi ve ondan türeyen özet bozulur.
// `fieldGroups.markLinked` yalnız TEK oturum içindeki tekrarı engeller, oturumlar arasını DEĞİL.
//
// **Belge türleri (GL #7).** `UploadModal`'ın elle dalıyla AYNI durum ve liste
// (`useDocumentFieldGroups` + `DocumentFieldGroupList`, hedefe göre `company`/`personnel`); kaydın
// durumu grupların üstünde TEK kez (`DocumentTypesStatus`, her açılışta bir kez tazeler). Bir
// kaynağın türü sunucuda reddedilirse yalnız o kaynağın grubu temizlenir; önceki kaynaklar zaten
// bağlandı (`markLinked` — "Kaydedildi" kartı, `Kaydet`i kilitlemez), `Kaydet` o kaynaktan devam eder. Kaynak dosyalar analize aittir — burada
// ASLA silinmez.

import {
  Alert,
  Button,
  Modal,
  Spinner,
  documentAnalysesApi,
  isTypeFieldRejection,
  resolveApiErrorMessage,
  supplierContractorApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { DocumentFieldGroupList, useDocumentFieldGroups } from './documents/DocumentFieldGroups';
import { DocumentTypesStatus } from './documents/DocumentTypeFields';

/** Hangi uca/alan-grubuna bağlanacağı — Belgeler (taşeron) ya da bir personelin sayfası.
 * `StrandedAnalysesStrip` de AYNI birliği aday süzgeci için kullanır (bkz. o dosya). */
export type StrandedTarget = { kind: 'contractor' } | { kind: 'personnel'; userUuid: string };

export interface StrandedLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysisUuid: string;
  companyId: number | null;
  target: StrandedTarget;
  /**
   * Çağıranın KENDİ listesinde ZATEN bağlı olan §8 `documentUuid`'leri (Belgeler'in taşeron
   * listesi / panelin kişi listesi). Bu kümedeki kaynaklar alan grubu olarak SUNULMAZ — aksi
   * hâlde ikinci bir link satırı doğardı (yukarıdaki F3 notu). Şerit bu kümeyi zaten "hâlâ
   * aksıyor mu" türetimi için hesaplıyor; burada AYNI küme ikinci kez kullanılır.
   */
  linkedDocumentUuids: Set<string>;
  /** Bu analizin TÜM kaynakları başarıyla bağlandığında (yeniden denenebilir kısmi başarı
   * DAHİL değil — yalnız hepsi bittiğinde). Çağıran listesi zaten geçersiz kılınır — şerit
   * bunu kendi `linkedDocumentUuids` türetimiyle otomatik yansıtır, ek bir bayrak TAŞIMAZ. */
  onLinked: () => void;
}

export function StrandedLinkModal({
  open,
  onOpenChange,
  analysisUuid,
  companyId,
  target,
  linkedDocumentUuids,
  onLinked,
}: StrandedLinkModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fieldGroups = useDocumentFieldGroups(target.kind === 'personnel' ? 'personnel' : 'company');
  const resetFieldGroups = fieldGroups.reset;

  // Bu oturumda hangi kaynakların ZATEN bağlandığı `fieldGroups.markLinked`'dedir — yeniden dene
  // (kısmi ağ hatası sonrası) aynı kaynağı ikinci kez linklemesin diye (`UploadModal` ile AYNI
  // savunma). Her açılışta sıfırlanır; girilen değerler (kaynak başına) korunur.
  useEffect(() => {
    if (open) {
      resetFieldGroups({ keepValues: true });
    }
  }, [open, resetFieldGroups]);

  const detail = useQuery({
    queryKey: supplierContractorKeys.analysis(companyId, analysisUuid),
    queryFn: () => documentAnalysesApi.getDocumentAnalysis(analysisUuid),
    enabled: open && companyId != null,
  });

  // ZATEN bağlı kaynaklar DÜŞÜLÜR (F3) — analiz kaydı bunu kendisi yapmaz. Kısmi bir
  // bağlamadan sonra modal yeniden açıldığında geriye YALNIZ hâlâ bekleyen kaynaklar kalır;
  // hepsi bağlıysa liste boşalır ve aşağıdaki "hepsi bağlandı" satırı görünür.
  const sources = useMemo(
    () =>
      (detail.data?.sources ?? []).filter(
        (source) => !linkedDocumentUuids.has(source.documentUuid),
      ),
    [detail.data, linkedDocumentUuids],
  );

  // Grup kimliği = kaynağın §8 `documentUuid`'i; ad = dosya adı.
  const groups = useMemo(
    () => sources.map((source) => ({ id: source.documentUuid, name: source.fileName })),
    [sources],
  );
  // Tamamlık = seçilebilir (aktif) bir tür + kural istiyorsa bitiş tarihi (GL #7).
  const allValid = groups.length > 0 && fieldGroups.allComplete(groups.map((group) => group.id));

  const linkMutation = useMutation({
    mutationFn: async () => {
      for (const source of sources) {
        if (fieldGroups.isLinkedNow(source.documentUuid)) {
          continue;
        }
        if (!fieldGroups.isComplete(source.documentUuid)) {
          continue; // Kaydet zaten devre dışı — savunma amaçlı.
        }
        const body = fieldGroups.linkBody(source.documentUuid, source.documentUuid);
        if (target.kind === 'personnel') {
          await supplierContractorApi.linkSupplierPersonnelDocument(target.userUuid, body);
        } else {
          await supplierContractorApi.linkSupplierDocument(body);
        }
        fieldGroups.markLinked(source.documentUuid);
      }
    },
    // Tür reddi (§27.6) YALNIZ işlenen kaynağa aittir — öncekiler zaten bağlandı.
    onError: (error) => {
      fieldGroups.handleLinkError(error);
    },
    onSuccess: () => {
      if (target.kind === 'personnel') {
        queryClient.invalidateQueries({
          queryKey: supplierContractorKeys.personnelDocuments(companyId, target.userUuid),
        });
      } else {
        queryClient.invalidateQueries({ queryKey: supplierContractorKeys.documents(companyId) });
        // Uygunluk özeti belgelerden türetilir → firma bilgisi (dashboard) de tazelenir.
        queryClient.invalidateQueries({ queryKey: supplierContractorKeys.contractor(companyId) });
      }
      onLinked();
    },
  });

  const typeRejected = linkMutation.isError && isTypeFieldRejection(linkMutation.error);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          linkMutation.reset();
        }
        onOpenChange(next);
      }}
      title={t('documents.stranded.cta')}
      size="lg"
      closeLabel={t('common.close')}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={linkMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => linkMutation.mutate()}
            loading={linkMutation.isPending}
            disabled={!allValid || linkMutation.isPending}
          >
            {t('upload.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {detail.isPending ? <Spinner label={t('common.loading')} /> : null}
        {detail.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(detail.error, t)}</Alert>
        ) : null}
        {/* Tür reddi kaynağın kendi seçicisinin altındadır — burada ikinci kez gösterilmez. */}
        {linkMutation.isError && !typeRejected ? (
          <Alert tone="danger">{resolveApiErrorMessage(linkMutation.error, t)}</Alert>
        ) : null}
        {/* Kaydın durumu TEK kez, grupların üstünde. */}
        {sources.length > 0 ? <DocumentTypesStatus entity={fieldGroups.entity} /> : null}
        {/* Süzgeçten sonra geriye kaynak kalmadıysa (şerit, liste tazelenmeden önce yeniden
            açıldı) boş bir gövde göstermeyiz — durumu SÖYLERİZ; `Kaydet` zaten kapalıdır. */}
        {detail.isSuccess && sources.length === 0 ? (
          <p className="text-sm text-muted">{t('documents.stranded.allLinked')}</p>
        ) : null}
        <DocumentFieldGroupList
          groups={groups}
          fieldGroups={fieldGroups}
          disabled={linkMutation.isPending}
        />
      </div>
    </Modal>
  );
}
